import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  IconButton,
  Alert
} from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { authApi } from '../services/api';
import { useTranslation } from 'react-i18next';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
      setError('');
    } catch (err) {
      console.error('Reset password error:', err);
      if (err.code === 'ERR_NETWORK') {
        setError(t('auth.errors.networkError'));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('auth.errors.resetPasswordFailed'));
      }
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        <IconButton 
          onClick={toggleTheme} 
          color="inherit"
          aria-label={isDarkMode ? t('common.lightMode') : t('common.darkMode')}
        >
          {isDarkMode ? <Brightness7 /> : <Brightness4 />}
        </IconButton>
      </Box>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          {t('auth.resetPassword')}
        </Typography>
        {success && (
          <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
            {t('auth.resetEmailSent')}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
            {error}
          </Alert>
        )}
        <Paper
          component="form"
          onSubmit={handleSubmit}
          sx={{ mt: 3, p: 3, width: '100%' }}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            label={t('common.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
          >
            {isLoading ? t('auth.resettingPassword') : t('auth.resetPassword')}
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2">
              {t('auth.backToSignIn')}
            </Link>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;